/**
 * ZoomPanController - Handles touch/mouse interactions for zoom and pan
 * 
 * Responsibilities:
 * - Handle touch gestures (pinch zoom, pan)
 * - Handle mouse interactions (double-click zoom, drag pan)
 * - Manage gesture state
 * - Emit zoom/pan events
 */
export class ZoomPanController {
    constructor(slideElement, config = {}) {
        this.slideElement = slideElement;
        this.config = {
            minZoom: 1,
            maxZoom: 3,
            doubleClickZoom: 2,
            // Double-tap detection thresholds (touchend-based fallback for iOS WKWebView
            // where preventDefault on touchstart suppresses the synthetic dblclick event).
            doubleTapMaxIntervalMs: 300,
            doubleTapMaxDistancePx: 30,
            tapMaxMovementPx: 10,
            // Guard window after a touch-based double-tap to swallow any redundant
            // dblclick that might still fire on desktop/simulator.
            doubleTapSuppressDblclickMs: 400,
            ...config
        };

        // Gesture state
        this.isPanning = false;
        this.lastTouchDistance = 0;
        this.lastTouchX = 0;
        this.lastTouchY = 0;
        this.lastMouseX = 0;
        this.lastMouseY = 0;
        this.pinchCenterX = 0;
        this.pinchCenterY = 0;

        // Double-tap detection state
        this.touchStartTime = 0;
        this.touchStartX = 0;
        this.touchStartY = 0;
        this.lastTapTime = 0;
        this.lastTapX = 0;
        this.lastTapY = 0;
        this.lastDoubleTapTime = 0;
        
        // Dependencies
        this.imageTransform = null;
        
        // Event listeners
        this.listeners = new Map();
        this.boundMethods = {};
        
        // Don't setup event listeners immediately - let setEnabled control this
    }
    
    /**
     * Set the image transform dependency
     */
    setImageTransform(imageTransform) {
        this.imageTransform = imageTransform;
    }
    
    /**
     * Add event listener
     */
    addEventListener(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
    }
    
    /**
     * Emit event to listeners
     */
    emit(event, data = {}) {
        const callbacks = this.listeners.get(event);
        if (callbacks) {
            callbacks.forEach(callback => callback(data));
        }
    }
    
    /**
     * Setup event listeners
     */
    setupEventListeners() {
        // Bind methods to preserve 'this' context
        this.boundMethods = {
            handleTouchStart: this.handleTouchStart.bind(this),
            handleTouchMove: this.handleTouchMove.bind(this),
            handleTouchEnd: this.handleTouchEnd.bind(this),
            handleMouseDown: this.handleMouseDown.bind(this),
            handleMouseMove: this.handleMouseMove.bind(this),
            handleMouseUp: this.handleMouseUp.bind(this),
            handleMouseLeave: this.handleMouseLeave.bind(this),
            handleDoubleClick: this.handleDoubleClick.bind(this)
        };
        
        // Touch events
        this.slideElement.addEventListener('touchstart', this.boundMethods.handleTouchStart, { passive: false });
        this.slideElement.addEventListener('touchmove', this.boundMethods.handleTouchMove, { passive: false });
        this.slideElement.addEventListener('touchend', this.boundMethods.handleTouchEnd, { passive: false });
        
        // Mouse events
        this.slideElement.addEventListener('mousedown', this.boundMethods.handleMouseDown);
        this.slideElement.addEventListener('mousemove', this.boundMethods.handleMouseMove);
        this.slideElement.addEventListener('mouseup', this.boundMethods.handleMouseUp);
        this.slideElement.addEventListener('mouseleave', this.boundMethods.handleMouseLeave);
        
        // Double click for zoom toggle
        this.slideElement.addEventListener('dblclick', this.boundMethods.handleDoubleClick);
    }
    
    /**
     * Remove event listeners
     */
    removeEventListeners() {
        // Touch events
        this.slideElement.removeEventListener('touchstart', this.boundMethods.handleTouchStart);
        this.slideElement.removeEventListener('touchmove', this.boundMethods.handleTouchMove);
        this.slideElement.removeEventListener('touchend', this.boundMethods.handleTouchEnd);
        
        // Mouse events
        this.slideElement.removeEventListener('mousedown', this.boundMethods.handleMouseDown);
        this.slideElement.removeEventListener('mousemove', this.boundMethods.handleMouseMove);
        this.slideElement.removeEventListener('mouseup', this.boundMethods.handleMouseUp);
        this.slideElement.removeEventListener('mouseleave', this.boundMethods.handleMouseLeave);
        this.slideElement.removeEventListener('dblclick', this.boundMethods.handleDoubleClick);
    }
    
    /**
     * Enable or disable interactions
     */
    setEnabled(enabled) {
        // Always remove first to avoid duplicate listeners
        this.removeEventListeners();
        
        if (enabled) {
            this.setupEventListeners();
        }
    }
    
    /**
     * Handle touch start
     */
    handleTouchStart(e) {
        if (!this.imageTransform) return;

        const state = this.imageTransform.getState();

        if (e.touches.length === 1) {
            // Record tap origin for double-tap detection (works regardless of pan state)
            this.touchStartTime = Date.now();
            this.touchStartX = e.touches[0].clientX;
            this.touchStartY = e.touches[0].clientY;
        }

        if (e.touches.length === 2) {
            // Two finger pinch zoom
            e.preventDefault();
            this.lastTouchDistance = this.getTouchDistance(e.touches);
            // Store center point for zoom
            this.pinchCenterX = (e.touches[0].clientX + e.touches[1].clientX) / 2;
            this.pinchCenterY = (e.touches[0].clientY + e.touches[1].clientY) / 2;
        } else if (e.touches.length === 1 && state.canPan) {
            // Single finger pan
            e.preventDefault();
            this.isPanning = true;
            this.lastTouchX = e.touches[0].clientX;
            this.lastTouchY = e.touches[0].clientY;

            this.emit('panStart');
        }
    }
    
    /**
     * Handle touch move
     */
    handleTouchMove(e) {
        if (!this.imageTransform) return;
        
        if (e.touches.length === 2 && this.lastTouchDistance > 0) {
            // Two finger pinch zoom
            e.preventDefault();
            
            const currentDistance = this.getTouchDistance(e.touches);
            const scale = currentDistance / this.lastTouchDistance;
            const currentZoom = this.imageTransform.getState().currentZoom;
            const newZoom = Math.max(this.config.minZoom, Math.min(this.config.maxZoom, currentZoom * scale));
            
            // Use the initial pinch center for zoom
            const rect = this.slideElement.getBoundingClientRect();
            const relativeCenterX = (this.pinchCenterX - rect.left) / rect.width;
            const relativeCenterY = (this.pinchCenterY - rect.top) / rect.height;
            
            this.emit('zoomRequested', {
                zoom: newZoom,
                centerX: relativeCenterX,
                centerY: relativeCenterY
            });
            
            this.lastTouchDistance = currentDistance;
        } else if (e.touches.length === 1 && this.isPanning) {
            // Single finger pan
            e.preventDefault();
            
            const deltaX = e.touches[0].clientX - this.lastTouchX;
            const deltaY = e.touches[0].clientY - this.lastTouchY;
            
            this.emit('panRequested', {
                deltaX,
                deltaY
            });
            
            this.lastTouchX = e.touches[0].clientX;
            this.lastTouchY = e.touches[0].clientY;
        }
    }
    
    /**
     * Handle touch end
     */
    handleTouchEnd(e) {
        if (e.touches.length < 2) {
            this.lastTouchDistance = 0;
        }

        if (e.touches.length === 0) {
            // All fingers lifted - stop panning
            if (this.isPanning) {
                this.isPanning = false;
                this.emit('panEnd');
            }

            // Touch-based double-tap detection. Required on iOS WKWebView when zoomed:
            // handleTouchStart calls preventDefault() to claim the pan gesture, which
            // suppresses WebKit's synthetic dblclick event. So we detect double-tap
            // ourselves from touchend timing.
            this.detectDoubleTap(e);
        }
    }

    /**
     * Detect double-tap from successive touchend events and trigger zoom toggle.
     * Called from handleTouchEnd when all fingers are lifted.
     */
    detectDoubleTap(e) {
        if (!e.changedTouches || e.changedTouches.length !== 1) return;
        if (!this.touchStartTime) return;

        const now = Date.now();
        const endX = e.changedTouches[0].clientX;
        const endY = e.changedTouches[0].clientY;

        // The current touch must look like a tap, not a swipe/drag
        const movedX = Math.abs(endX - this.touchStartX);
        const movedY = Math.abs(endY - this.touchStartY);
        const isTap = movedX <= this.config.tapMaxMovementPx
                   && movedY <= this.config.tapMaxMovementPx;
        if (!isTap) {
            this.lastTapTime = 0;
            return;
        }

        // Check whether this tap closes a double-tap with the previous one
        const elapsedSinceLastTap = now - this.lastTapTime;
        const distFromLastTap = Math.hypot(endX - this.lastTapX, endY - this.lastTapY);

        if (this.lastTapTime > 0
            && elapsedSinceLastTap <= this.config.doubleTapMaxIntervalMs
            && distFromLastTap <= this.config.doubleTapMaxDistancePx) {
            this.lastTapTime = 0;
            this.lastDoubleTapTime = now;
            this.triggerZoomToggle(endX, endY);
            return;
        }

        // Otherwise this is the first tap of a potential double-tap
        this.lastTapTime = now;
        this.lastTapX = endX;
        this.lastTapY = endY;
    }

    /**
     * Toggle zoom at a given viewport coordinate. Shared by dblclick (desktop) and
     * the touchend-based double-tap detector (iOS WKWebView).
     */
    triggerZoomToggle(clientX, clientY) {
        if (!this.imageTransform) return;

        const state = this.imageTransform.getState();

        if (state.isZoomed) {
            this.emit('resetZoomRequested');
            return;
        }

        const rect = this.slideElement.getBoundingClientRect();
        const centerX = (clientX - rect.left) / rect.width;
        const centerY = (clientY - rect.top) / rect.height;

        this.emit('zoomRequested', {
            zoom: this.config.doubleClickZoom,
            centerX,
            centerY
        });
    }
    
    /**
     * Get distance between two touches
     */
    getTouchDistance(touches) {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }
    
    /**
     * Handle mouse down
     */
    handleMouseDown(e) {
        if (!this.imageTransform) return;
        
        const state = this.imageTransform.getState();
        if (!state.canPan) return;
        
        this.isPanning = true;
        this.lastMouseX = e.clientX;
        this.lastMouseY = e.clientY;
        
        this.emit('panStart');
        e.preventDefault();
    }
    
    /**
     * Handle mouse move
     */
    handleMouseMove(e) {
        if (!this.isPanning) return;
        
        const deltaX = e.clientX - this.lastMouseX;
        const deltaY = e.clientY - this.lastMouseY;
        
        this.emit('panRequested', {
            deltaX,
            deltaY
        });
        
        this.lastMouseX = e.clientX;
        this.lastMouseY = e.clientY;
    }
    
    /**
     * Handle mouse up
     */
    handleMouseUp() {
        if (this.isPanning) {
            this.isPanning = false;
            this.emit('panEnd');
        }
    }
    
    /**
     * Handle mouse leave
     */
    handleMouseLeave() {
        if (this.isPanning) {
            this.isPanning = false;
            this.emit('panEnd');
        }
    }
    
    /**
     * Handle double click
     */
    handleDoubleClick(e) {
        if (!this.imageTransform) return;

        e.preventDefault();

        // Suppress redundant dblclick on platforms where the touchend-based detector
        // has already handled this double-tap (rare in practice — preventDefault on
        // touchstart typically eats dblclick — but defensive against future platform changes).
        if (this.lastDoubleTapTime
            && Date.now() - this.lastDoubleTapTime <= this.config.doubleTapSuppressDblclickMs) {
            return;
        }

        this.triggerZoomToggle(e.clientX, e.clientY);
    }
    
    /**
     * Get current gesture state
     */
    getGestureState() {
        return {
            isPanning: this.isPanning
        };
    }
    
    /**
     * Clean up resources
     */
    destroy() {
        this.removeEventListeners();
        this.listeners.clear();
        this.imageTransform = null;
    }
}